import { createRouter, createWebHistory } from 'vue-router';
import Home from '@/views/Home.vue';
import ConfirmLines from '@/views/ConfirmLines.vue';
import History from '@/views/History.vue';
import Picks from '@/views/Picks.vue';
import auth from '@/utils/auth';

const routes = [
  { path: '/', name: 'Home', component: Home },
  { path: '/history', name: 'History', component: History },
  // Old path; the tab and every deep link used to live here.
  { path: '/games', redirect: (to) => ({ path: '/history', query: to.query }) },
  // Lazy so the detail page and its heavier children stay out of the Home bundle.
  // Reached from Home, not from the History tab -- unrelated screen, kept here.
  {
    path: '/games/:id',
    name: 'Game',
    component: () => import('@/views/GamePage.vue'),
  },
  // Lazy: the whole field for a season is a heavier screen than the tabs that
  // load on first paint, and it is reached deliberately.
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: () => import('@/views/Leaderboard.vue'),
  },
  { path: '/picks', name: 'Picks', component: Picks },
  {
    path: '/confirm',
    name: 'Confirm Lines',
    component: ConfirmLines,
    meta: { adminOnly: true },
  },
  {
    path: '/admin/health',
    name: 'Data Health',
    component: () => import('@/views/AdminHealth.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/admin/users',
    name: 'Users',
    component: () => import('@/views/AdminUsers.vue'),
    meta: { adminOnly: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Back to Home restores where you were in the list; a fresh page starts at
  // the top; an in-page anchor clears the fixed app header.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    // Adding a Pool comparison or changing its week only updates query state.
    // Keep the current section in view instead of jumping back to the hero.
    if (to.name === 'Leaderboard' && from.name === 'Leaderboard' && !to.hash && !from.hash) {
      return false;
    }
    // The Results week picker stores its selection in the URL. That is an
    // in-place data change, not a new screen, so keep the dropdown under the
    // user's finger instead of jumping the page back to the top.
    if (to.name === 'History' && from.name === 'History') {
      const keys = new Set([...Object.keys(to.query), ...Object.keys(from.query)]);
      const onlyWeekChanged = to.query.week !== from.query.week &&
        [...keys].every((key) => key === 'week' || to.query[key] === from.query[key]);
      if (onlyWeekChanged) return false;
    }
    if (to.hash) {
      const header = document.querySelector('.app-header');
      return { el: to.hash, top: (header?.offsetHeight || 56) + 8 };
    }
    return { top: 0 };
  },
});

// Confirming the week's lines is an admin-only action (the backend enforces it
// with a 403). Keep non-admins off the screen entirely rather than letting it
// load and fail. `restoreSession` is reentrant and resolves the same request
// App.vue's mount already kicked off, so a direct load of /confirm waits for
// the real answer instead of bouncing on the not-yet-restored null user.
router.beforeEach(async (to) => {
  if (!to.meta.adminOnly) return true;
  if (!auth.state.ready) await auth.restoreSession();
  return auth.state.user?.admin ? true : { path: '/' };
});

export default router;
